import { createContext, useContext } from 'react';
import type { AudioBufferCache } from '../audio/assets/bufferCache';
import type { AudioEngine } from '../audio/audioEngine';
import type { AVSessionController } from '../av/sessionController';
import type { WavExporter } from '../audio/wavExporter';
import type { VideoExporter } from '../video/videoExporter';
import type { AnalysisStore } from '../visualizer/analysisStore';
import type { ProviderRegistry } from '../providers/registry';
import type { ProjectRepository } from '../storage/projectRepository';
import type { RecipeRepository } from '../storage/recipeRepository';
import type { AnimationBus } from './animationBus';
import type { MotionComposerStore } from './motionComposer';
import type { SelectionStore } from './selection';
import type { SyncSelectionStore } from './syncSelection';
import type { Observable } from '../lib/store';
import type { UnsavedWork } from './recovery';

export interface Services {
  repo: ProjectRepository;
  registry: ProviderRegistry;
  audio: AudioEngine;
  exporter: WavExporter;
  animation: AnimationBus;
  /** Decoded samples/clips shared by playback, export, previews and waveforms. */
  buffers: AudioBufferCache;
  /** Decodes stored image assets for the scene renderer (lazy; the renderer caches textures). */
  images: { load(assetId: string): Promise<ImageBitmap> };
  selection: SelectionStore;
  /** Motion Composer plan (UI state; Phase 3C). */
  motion: MotionComposerStore;
  /** Locally stored motion recipes (Phase 3C). */
  recipes: RecipeRepository;
  /** Audio-visual session playback: the single session clock (Phase 4A). */
  sync: AVSessionController;
  /** Sync workspace selection (UI state; Phase 4A). */
  syncSelection: SyncSelectionStore;
  /** Video export of sync sessions (Phase 4B; one export at a time). */
  videoExporter: VideoExporter;
  /** Measured audio features for visualizers, shared by preview and export (Phase 4C). */
  analysis: AnalysisStore;
  /** Edits whose final save failed when their project closed, offered as a backup file. */
  recovery: Observable<UnsavedWork | null>;
}

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (!services) throw new Error('useServices must be used inside <ServicesProvider>.');
  return services;
}
